// ============================================================================
// MOCK DATA FUNCTIONS - REPLACE THESE WITH REAL API CALLS
// ============================================================================
// Pattern: get{Cloud}{ResourceType}List()
// Example: getGCPVMList(), getAWSStorageList(), getAzureDatabaseList()
// 
// To use with real API:
// 1. Replace the mock data with fetch() or axios calls
// 2. Use the cloudProvider parameter to call the correct endpoint
// 3. Keep the same return type structure
// ============================================================================

import { Database, HardDrive, Monitor } from "lucide-react";
import { myLocalStorage } from "../../StorageHelper";
import { formatDate } from "../formatTimeAgo";
import { fetchAllPublishedTypes, fetchAzureUserConfig, fetchK8Resources, getAllAzureClusters, getAllAzureDatabase, getAllAzureResources, getAllAzureStorage, getAzureAllRoles, getK8Roles, specificAzureUser } from "./Api/tenantazureapi";
import { fetchGCPUserConfig, getGCPAllCluster, getGCPAllDatabases, getGCPAllRoles, getGCPAllStorages, getGCPAllVms, specificGCPUser } from "./Api/tenantgcpapi";
import { Role } from "./interface";
import { SiKubernetes } from "react-icons/si";
import { fetchAWSClusterList, fetchAWSEC2List, fetchAWSRDSList, fetchAWSS3List, fetchUserConfiguration, getAWSAllPoliciesList, specificAwsUser } from "./Api/tenantawsapi";

export interface Resource {
  id: string;
  name: string;
  type: "vm" | "storage" | "database" | "kubernetes";
  project?: string; // For GCP
  region: string;
  createdAt: string;
  status: "running" | "stopped" | "available";
  description: string;
  details: object
}

export interface UserProjectAccess {
  userName: string;
  projectId: string;
  accountEmail: string;
  syncedAt: string;
  roles: string[];
}

export const CLOUD_REFRESH_INTERVAL_MS = 5000;

export const haveSameRoles = (current: Role[] = [], next: Role[] = []) =>
  JSON.stringify(current) === JSON.stringify(next);

export const projects = [
  { id: "whiteswan-vm-allocation", name: "whiteswan-vm-allocation" },
  { id: "production-analytics", name: "production-analytics" },
  { id: "dev-environment", name: "dev-environment" },
];


export const formatTime = (isHasAccess: any, cloudProvider: any) => {
  const accessRecord = Array.isArray(isHasAccess) ? isHasAccess[0] : isHasAccess;

  const privilegeLevel =
    accessRecord?.privilegeLevel ??
    accessRecord?.privilegeType ??
    accessRecord?.privilege ??
    accessRecord?.accessPrivilege;

  if (typeof privilegeLevel === "string" && privilegeLevel.toUpperCase() === "PERMANENT") {
    return "Permanent";
  }

  if (accessRecord?.selectedTime === 0 || accessRecord?.expiryTimeAmount === 0) {
    return "Permanent";
  }

  let isValidTo = accessRecord?.validTo;

  if (cloudProvider === "gcp" || cloudProvider === "aws") {
    isValidTo = accessRecord?.accessTimeOverAt;
  }

  if (!isHasAccess || !isValidTo) return "Invalid input";

  let validTo: any;
  const timestamp = isValidTo;
  console.log(timestamp);
  if (typeof timestamp === "string") {
    const isUTC = timestamp.endsWith("Z");
    const normalizedTimestamp = isUTC ? timestamp : `${timestamp}Z`;
    validTo = new Date(normalizedTimestamp);
  } else {
    validTo = new Date(timestamp);
  }

  if (isNaN(validTo.getTime())) return "Invalid date";

  const now = new Date();
  const utcNow = new Date(
    Date.UTC(
      now.getUTCFullYear(),
      now.getUTCMonth(),
      now.getUTCDate(),
      now.getUTCHours(),
      now.getUTCMinutes(),
      now.getUTCSeconds(),
      now.getUTCMilliseconds()
    )
  );

  let diffMs = validTo.getTime() - utcNow.getTime();

  if (diffMs <= 0) return "00:00";

  const totalSeconds = Math.floor(diffMs / 1000);
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;

  return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
};

const formatDisplayDate = (value: unknown): string => {
  if (value == null || value === "") return "-";
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString("en-GB");
  }
  if (value instanceof Date) {
    return value.toLocaleDateString("en-GB");
  }
  if (typeof value === "object") {
    const candidate =
      (value as { createdAt?: unknown; epochSecond?: number }).createdAt ??
      (value as { epochSecond?: number }).epochSecond;
    if (candidate != null) {
      return formatDisplayDate(candidate);
    }
    return "-";
  }
  return String(value);
};

const normalizeAwsPolicyRoles = (policies: unknown): string[] => {
  if (!Array.isArray(policies)) return [];
  return policies
    .map((policy) => {
      if (typeof policy === "string") return policy;
      if (policy && typeof policy === "object") {
        const policyObj = policy as { policyName?: string; policyARN?: string };
        return policyObj.policyName || policyObj.policyARN || "";
      }
      return "";
    })
    .filter(Boolean);
};

export const getResourceTypes = (cloudProvider: string) => {
  switch (cloudProvider?.toLowerCase()) {
    case "aws":
      return [
        { id: "ec2", name: "EC2 Instances", icon: Monitor, color: "bg-yellow-500/10 text-yellow-600 dark:text-yellow-400" },
        { id: "s3", name: "S3 Storage", icon: HardDrive, color: "bg-orange-500/10 text-orange-600 dark:text-orange-400" },
        { id: "rds", name: "RDS Databases", icon: Database, color: "bg-green-500/10 text-green-600 dark:text-green-400" },
        { id: "kubernetes", name: "Kubernetes", icon: SiKubernetes, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },

      ];

    default:
      return [
        { id: "vm", name: "Virtual Machines", icon: Monitor, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
        { id: "storage", name: "Storages", icon: HardDrive, color: "bg-orange-500/10 text-orange-600 dark:text-orange-400" },
        { id: "database", name: "Databases", icon: Database, color: "bg-green-500/10 text-green-600 dark:text-green-400" },
        { id: "kubernetes", name: "Kubernetes", icon: SiKubernetes, color: "bg-blue-500/10 text-blue-600 dark:text-blue-400" },
      ];
  }
};

// ============================================================================
// GCP RESOURCES
// ============================================================================

export const getGCPVMList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  // Fetch all VMs
  const response = await getGCPAllVms(latestTenant, latestProjectId);

  // Convert the API response into the required structure
  const converted = (response || []).map((item: any, index: number) => ({
    id: item.id,
    name: item.name || "-",
    type: "vm",
    project: item.projectId || "-",
    region: item.zone || "-",
    createdAt: item.createdAt
      ? new Date(item.createdAt).toLocaleDateString("en-GB") // Convert date → dd/mm/yyyy
      : "-",
    status: (item.status || "UNKNOWN").toLowerCase(),
    description: "Google Cloud Virtual Machine",
    details: item,
    publish: item.isManagedVirtualMachine
  }));

  return converted;
};


export const getGCPStorageList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  // Fetch all GCP storage buckets
  const response = await getGCPAllStorages(latestTenant, latestProjectId);

  // Convert API response to your required structure
  const converted = (response || []).map((item: any, index: number) => ({
    id: item.id,
    name: item.name || "-",
    type: "storage",
    project: item.projectId || "-",
    region: item.location?.toLowerCase() || "-",
    createdAt: item.createdTime
      ? new Date(Number(item.createdTime)).toLocaleDateString("en-GB") // Convert timestamp → dd/mm/yyyy
      : "-",
    status: "available", // GCS buckets are always available unless explicitly deleted
    description: `Google Cloud Storage (${item.storageClass || "STANDARD"})`,
    details: item,
    publish: item.isManagedStorageBucket
  }));

  return converted;
};

export const getGCPDatabaseList = async () => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  // Fetch GCP database instances
  const response = await getGCPAllDatabases(latestTenant, latestProjectId);

  // Convert API response to desired structure
  const converted = (response || []).map((item: any, index: number) => ({
    id: item.id,
    name: item.name || "-",
    type: "database",
    project: item.projectId || "-",
    region: item.region || "-",
    createdAt: "-", // not present in API, so placeholder
    status: item.state?.toLowerCase() || "-",
    description: `Cloud SQL ${item.databaseVersion?.replace("_", " ") || "Database Instance"}`,
    details: item,
    publish: item.isManagedDatabase
  }));

  return converted;
};

export const getGCPKubernetesList = async () => {

  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  // Fetch GCP database instances
  const response = await getGCPAllCluster(latestTenant, latestProjectId);
  const converted = (response || []).map((item: any, index: number) => ({
    id: item.gcpClusterId,
    name: item.name || "-",
    type: "kubernetes",
    project: item.projectId || "-",
    region: item.location || "-",
    createdAt: "-",
    status: "running",
    description: item.description,
    details: item,
    publish: item.isManagedKubernetesCluster
  }));
  return converted;
}

export const getGCPRolesList = async (selectedResourceType: string): Promise<Role[]> => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");

  // Fetch GCP roles
  const response = await getGCPAllRoles(latestTenant, latestProjectId, selectedResourceType);

  // Convert API response to Role interface
  const converted: Role[] = (response || []).map((item: any) => ({
    id: item.roleId || item.id?.toString() || "-",
    name: item.roleName || "Unnamed Role",
    description: item.description || "",
    permissions: item.permissions || [],
    verbs: [],
    resources: [],
    apiGroups: [],
    details: item,
    path: "",
  }));

  return converted;
};



export const getGCPUserProjectAccess = async (
  project: string,
  userEmail: string
): Promise<UserProjectAccess | null> => {
  try {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const response = await specificGCPUser(latestTenant, project, userEmail);

    const user = Array.isArray(response) ? response[0] : response;

    if (!user) {
      console.warn("No user found in response from specificGCPUser");
      return null;
    }

    // ✅ Extract roleName safely and clean
    const formattedRoles =
      (user.roles || [
        { roleName: "roles/owner" },
        { roleName: "roles/editor" },
        { roleName: "roles/viewer" },
      ])
        .map((r: any) => r.roleName || r)
        .filter(Boolean)
        .map((role: string) => role.split("/").pop() || role);

    return {
      userName: user.userName || "N/A",
      projectId: user.projectId || project || "N/A",
      accountEmail: user.email || user.gcpUserEmail || userEmail || "N/A",
      syncedAt: user.syncedAt
        ? new Date(user.syncedAt).toLocaleString()
        : new Date().toLocaleString(),
      roles: formattedRoles,
    };
  } catch (error) {
    console.error("Error fetching GCP user project access:", error);
    return null;
  }
};



// ============================================================================
// AWS RESOURCES
// ============================================================================

export const getAWSEC2List = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
  // Don't call the API until an AWS account is selected, otherwise accountId=null is sent.
  if (!selectedAwsAccount) return [];

  try {
    const response = await fetchAWSEC2List(latestTenant, selectedAwsAccount);
    if (!Array.isArray(response)) return [];

    return response.map((instance, index) => ({
      id: `aws-ec2-${index + 1}`,
      name: instance.instanceName || instance.instanceId || "Unknown Instance",
      arn: instance.instanceArn || instance.instanceARN || "N/A",
      type: "ec2",
      region: instance.availabilityZone?.slice(0, -1) || "Unknown Region",
      createdAt: instance.launchTime
        ? new Date(instance.launchTime).toLocaleDateString("en-GB")
        : "N/A",
      status: instance.instanceState || "unknown",
      description: `Amazon EC2 Instance (${instance.instanceType || "N/A"})`,
      details: instance
    }));
  } catch (error) {
    console.error("Error fetching AWS EC2 instances:", error);
    return [];
  }
}

export const getAWSS3List = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
  if (!selectedAwsAccount) return [];

  try {
    const response = await fetchAWSS3List(latestTenant, selectedAwsAccount);
    if (!Array.isArray(response)) return [];

    return response.map((bucket, index) => ({
      id: `aws-s3-${index + 1}`,
      name: bucket.bucketName || "Unknown Bucket",
      arn: "arn:aws:s3:::" + bucket.bucketName || "N/A",
      type: "s3",
      region: bucket.region || "Unknown Region",
      createdAt: bucket.creationDate
        ? new Date(bucket.creationDate).toLocaleDateString("en-GB")
        : "N/A",
      status: bucket.isManagedS3 ? "available" : "unmanaged",
      description: "Amazon S3 Bucket",
      details: bucket
    }));
  } catch (error) {
    console.error("Error fetching AWS S3 buckets:", error);
    return [];
  }
}

export const getAWSRDSList = async (region?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
  if (!selectedAwsAccount) return [];

  try {
    const response = await fetchAWSRDSList(latestTenant, selectedAwsAccount);
    if (!Array.isArray(response)) return [];

    return response.map((rds: any, index: number) => ({
      id: `aws-rds-${index + 1}`,
      name: rds.rdsIdentifierName || "Unknown RDS Instance",
      arn: rds.rdsDBInstanceArn || "N/A",
      type: "rds",
      engine: rds.rdsEngine || "Unknown Engine",
      region: rds.rdsAvailabilityZone || region || "Unknown Region",
      createdAt: rds.rdsInstanceCreatedTime
        ? new Date(rds.rdsInstanceCreatedTime).toLocaleDateString("en-GB")
        : "N/A",
      status: rds.rdsInstanceStatus || "unknown",
      masterUser: rds.rdsInstanceMasterUserName || "N/A",
      description: `Amazon RDS Instance (${rds.rdsEngine || "DB"})`,
      isManaged: !!rds.isManagedRDS,
      details: rds
    }));
  } catch (error) {
    console.error("Error fetching AWS RDS instances:", error);
    return [];
  }
};


export const buildAwsSelectedPolicies = (
  policies: Array<{ id?: string; name?: string; details?: Record<string, unknown> }>,
  tenantName: string,
) =>
  policies.map((policy) => {
    const details = (policy.details ?? policy) as {
      id?: number;
      policyName?: string;
      policyARN?: string;
    };

    return {
      id: details.id,
      policyName: details.policyName ?? policy.name ?? "",
      policyARN: details.policyARN ?? policy.id ?? "",
      tenantName,
    };
  });

export const toAwsPrivilegeLevel = (accessType: string): "PERMANENT" | "DYNAMIC" =>
  accessType?.toUpperCase() === "PERMANENT" ? "PERMANENT" : "DYNAMIC";

export const getAWSPoliciesList = async (
  accountId?: string,
  resourceType?: string
): Promise<Role[]> => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedAwsAccount = accountId || myLocalStorage.getItem("selectedAwsAccount");

  if (!latestTenant || !selectedAwsAccount) {
    console.warn("Missing tenant or AWS account for published policies list");
    return [];
  }

  try {
    const response = await getAWSAllPoliciesList(
      latestTenant,
      selectedAwsAccount,
      resourceType
    );
    const policies = Array.isArray(response) ? response : [];

    return policies.map((item: any) => ({
        id: item.policyARN || "-",
        name: item.policyName || "Unnamed Policy",
        description: item.policyARN || "",
        permissions: item.permissions || [],
        verbs: [],
        resources: [],
        apiGroups: [],
        details: item,
        path: item.policyARN,
      }));
  } catch (error) {
    console.error("Failed to load published AWS policies:", error);
    return [];
  }
};


export const getAWSKubernetesList = async (region?: string) => {
  try {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
    if (!selectedAwsAccount) return [];
    const response = await fetchAWSClusterList(latestTenant, selectedAwsAccount);
    const clusterList = Array.isArray(response) ? response : [];

    return clusterList.map((item: any) => ({
      id: item.clusterArn || String(item.id ?? item.clusterArn),
      dbId: item.id,
      name: item.clusterName || "-",
      arn: item.clusterArn || "",
      type: "kubernetes" as const,
      region: item.region || "-",
      createdAt: formatDisplayDate(item.createdAt),
      status: (item.status?.toLowerCase() === "active" ? "running" : "available") as "running" | "available",
      description: `EKS cluster in ${item.region || "unknown region"}`,
      details: {
        ...item,
        name: item.clusterName,
        clusterArn: item.clusterArn,
      },
      publish: item.isManaged,
      clusterArn: item.clusterArn,
    }));
  } catch (error) {
    console.error("Error fetching AWS Kubernetes clusters:", error);
    return [];
  }
};

export const getAWSUserProjectAccess = async (arn: string): Promise<UserProjectAccess | null> => {
  try {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
    if (!latestTenant || !selectedAwsAccount) {
      console.warn("Missing tenant or selected AWS account.");
      return null;
    }

    const response = await specificAwsUser(latestTenant, arn, selectedAwsAccount);

    const user = Array.isArray(response) ? response[0] : response;

    if (!user) {
      console.warn("No user found in response from specificAzureUser");
      return null;
    }

    return {
      userName: user.username || "N/A",
      projectId: user.accountId || "N/A",
      accountEmail: user.arn || "N/A",
      syncedAt: user.createDate ? new Date(user.createDate).toLocaleString() : "N/A",
      roles: normalizeAwsPolicyRoles(user.userPolicies),
    };
  } catch (error) {
    console.error("Error fetching Azure user project access:", error);
    return null;
  }
}

// ============================================================================
// AZURE RESOURCES
// ============================================================================

export const getAzureVMList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;


  const response = await getAllAzureResources(latestTenant, subId);


  const azureVMs =
    response
      ?.filter((r: any) => r.resourceType === "Microsoft.Compute/virtualMachines")
      ?.map((vm: any, index: number) => {
        const createdDate = vm.syncedAt
          ? new Date(vm.syncedAt).toLocaleDateString("en-GB") // format dd/mm/yyyy
          : "N/A";

        return {
          id: vm.id,
          name: vm.name || "Unknown VM",
          type: "vm",
          region: vm.region || "unknown",
          createdAt: createdDate,
          status: vm.powerState?.replace("PowerState/", "") || "unknown",
          description: "Azure Virtual Machine",
          details: vm,
          publish: vm.published,
          instanceId: vm.instanceId,
          azureUserId: vm.azureUserId,
          subscriptionId: vm.subscriptionId,
        };
      }) || [];

  return azureVMs;
};


export const getAzureStorageList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  // Fetch all Azure storage resources
  const response = await getAllAzureStorage(latestTenant, subId);

  // 🧠 Convert each storage account to your unified format
  const azureStorageAccounts =
    response
      ?.filter((r: any) => r.resourceType === "Microsoft.Storage/storageAccounts")
      ?.map((storage: any, index: number) => {
        const createdDate = storage.createdDate
          ? new Date(storage.createdDate).toLocaleDateString("en-GB") // dd/mm/yyyy
          : "N/A";

        // Determine storage description
        const isBlob = storage.kind?.toLowerCase().includes("blob");
        const description = isBlob ? "Azure Blob Storage" : "Azure Storage Account";

        return {
          id: storage.id,
          name: storage.storageAccountName || "Unknown Storage",
          type: "storage",
          region: storage.region || "unknown",
          createdAt: createdDate,
          status: storage.publicAccess === "Enabled" ? "available" : "restricted",
          description,
          details: storage,
          publish: storage.published,
          azureStorageAccountId: storage.azureStorageAccountId,
          azureUserId: storage.azureUserId,
          subscriptionId: storage.subscriptionId,
        };
      }) || [];

  return azureStorageAccounts;
};


export const getAzureDatabaseList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureDatabase(latestTenant, subId);

  // Convert API response to uniform format
  const formattedList = Array.isArray(response)
    ? response.map((db, index) => ({
      id: db.id,
      name: db.databaseName || db.name || "unknown-database",
      type: "database",
      region: db.region || db.location || "unknown-region",
      createdAt: db.createdDate
        ? new Date(db.createdDate).toLocaleDateString("en-GB") // DD/MM/YYYY
        : "unknown-date",
      status: db.status || db.state || "running",
      description:
        db.resourceType?.includes("Cosmos")
          ? "Azure Cosmos DB"
          : db.resourceType?.includes("postgres")
            ? "Azure Database for PostgreSQL"
            : db.resourceType?.includes("sql")
              ? "Azure SQL Database"
              : "Azure Database",
      publish: db.published,
      instanceId: db.instanceId,
      azureUserId: db.azureUserId,
      subscriptionId: db.subscriptionId,
    }))
    : [];

  return formattedList;
};


export const getAzureKubernetesList = async (resourceGroup?: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  const response = await getAllAzureClusters(latestTenant, subId);

  // Convert API response to uniform format
  const formattedList = Array.isArray(response)
    ? response.map((db, index) => ({
      id: db.id,
      name: db.name || "unknown-cluster",
      type: "kubernetes",
      region: db.regionName,
      createdAt: db.createdDate
        ? new Date(db.createdDate).toLocaleDateString("en-GB") // DD/MM/YYYY
        : "unknown-date",
      status: "running",
      description: "",
      azureId: db.azureId,
      subscriptionId: db.subscriptionId,
      publish: db.published,
      azureUserId: db.azureUserId,
      details: db,
    }))
    : [];

  return formattedList;
}


export const getAzureRoleList = async (selectedResourceType: string, userId: string, resourceId: string): Promise<Role[]> => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;

  // Fetch GCP roles
  const response = await getAzureAllRoles(resourceId, userId, selectedResourceType);

  // Convert API response to Role interface
  const converted: Role[] = (response || []).map((item: any) => ({
    id: item.roleId || item.azureRolePathId?.toString() || "-",
    name: item.roleName || "Unnamed Role",
    description: item.description || "",
    permissions: item.permissions || [],
    verbs: [],
    resources: [],
    apiGroups: [],
    details: item,
    path: item.azureRolePathId,
  }));

  return converted;
};

export const getAzureUserProjectAccess = async (
  azureId: string
): Promise<UserProjectAccess | null> => {
  try {
    const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
    const selectedSubListStr = myLocalStorage.getItem("latestSubList");
    const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
    const subId = selectedSubList?.azureSubscriptionId;

    if (!latestTenant || !azureId || !subId) {
      console.warn("Missing tenant, azureId, or subscription ID.");
      return null;
    }

    // ✅ Await the async call
    const response = await specificAzureUser(latestTenant, azureId, subId);

    // Handle case where API returns an array
    const user = Array.isArray(response) ? response[0] : response;

    if (!user) {
      console.warn("No user found in response from specificAzureUser");
      return null;
    }

    return {
      userName: user.displayName || "N/A",
      projectId: user.userPrincipalName || "N/A",
      accountEmail: user.azureUserId || "N/A",
      syncedAt: user.syncedAt ? new Date(user.syncedAt).toLocaleString() : "N/A",
      roles: user.roleDefinitions || [],
    };
  } catch (error) {
    console.error("Error fetching Azure user project access:", error);
    return null;
  }
};



export const getAzureUserConfig = async (email: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const selectedSubListStr = myLocalStorage.getItem("latestSubList");
  const selectedSubList = selectedSubListStr ? JSON.parse(selectedSubListStr) : null;
  const subId = selectedSubList?.azureSubscriptionId;
  const response = await fetchAzureUserConfig(latestTenant, email);
  return response;
}

export const getGCPUserConfig = async (email: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
  const response = await fetchGCPUserConfig(latestTenant, email);
  return response;
}

export const getAWSUserConfig = async (email: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const response = await fetchUserConfiguration(latestTenant, email);
  return response;
}

export const resolveK8SubjectName = async (
  cloudProvider: string,
  email: string,
): Promise<{ upn: string; displayName: string }> => {
  const provider = (cloudProvider || "").toLowerCase();

  if (provider === "aws") {
    const selectedAwsAccount = myLocalStorage.getItem("selectedAwsAccount");
    const response = await getAWSUserConfig(email);
    const configs = Array.isArray(response) ? response : response ? [response] : [];
    const config = selectedAwsAccount
      ? configs.find((item: any) => item.awsAccountId === selectedAwsAccount) || configs[0]
      : configs[0];

    if (!config) {
      return { upn: "", displayName: "" };
    }

    const upn = config.awsAccountARN?.includes(":user/")
      ? config.awsAccountARN
      : config.awsAccountId && config.awsUserName
        ? `arn:aws:iam::${config.awsAccountId}:user/${config.awsUserName}`
        : config.awsUserName || "";

    return {
      upn: upn || "",
      displayName: config.awsUserName || config.awsAccountName || "",
    };
  }

  if (provider === "gcp") {
    const response = await getGCPUserConfig(email);
    if (!response?.gcpAccountEmail) {
      return { upn: "", displayName: "" };
    }
    return {
      upn: response.gcpAccountEmail,
      displayName: response.gcpUserName || response.gcpAccountEmail,
    };
  }

  const response = await getAzureUserConfig(email);
  if (!response) {
    return { upn: "", displayName: "" };
  }
  return {
    upn: response.azureUserUpn || response.upn || "",
    displayName: response.displayName || response.azureUserUpn || "",
  };
};


export const getK8ResourceTypes = async () => {
  const response = await fetchAllPublishedTypes();
  return response;
}

export const getK8Resources = async (clusterId: string, type: string, cloudprovider: string) => {
  const latestTenant = myLocalStorage.getItem("latestTenant")?.tenantName;
  const latestProjectId = myLocalStorage.getItem("gcpSelectedProjectId");
  const response = await fetchK8Resources(latestTenant, clusterId, type, cloudprovider);
  return response;
}


export const getK8ApplicableRoles = async (name: string, payload: any): Promise<Role[]> => {

  const response = await getK8Roles(name, payload);

  const converted: Role[] = (response || []).map((item: any) => ({
    id: item.roleId || item.roleUid || item.id?.toString() || "-",
    name: item.roleName || "Unnamed Role",
    description: item.description || "",
    permissions: item.permissions || [],
    verbs: [],
    resources: [],
    apiGroups: [],
    details: item,
    path: "",
    roleKind: item.roleKind,
    roleLevel: item.roleLevel,
    roleName: item.roleName,
    roleUid: item.roleUid
  }));

  return converted;
};
